import BusinessIcon from '@mui/icons-material/Business'
import Close from '@mui/icons-material/Close'
import CropFreeIcon from '@mui/icons-material/CropFree'
import PersonIcon from '@mui/icons-material/Person'
import VerifiedIcon from '@mui/icons-material/Verified'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'
import CardImage from 'src/components/CardImage'
import CardFactsStrip, {
  FactChip
} from 'src/components/LocationSearchCard/CardFactsStrip'
import PromoBadge, { promoBadgeContent } from 'src/components/Promo/PromoBadge'
import { useAdminHostBadge } from 'src/components/SearchManager/AdminHostBadges'
import { isVehicleLocationType } from 'src/constants/pillarBoundaries'
import { useFilterProvider } from 'src/context/FilterProvider'
import useBreakpoints from 'src/hooks/useBreakpoints'
import useCardPrice from 'src/hooks/useCardPrice'
import useCommon from 'src/hooks/useCommon'
import useConfigSummary from 'src/hooks/useConfigSummary'
import { numberRounedUpWithoutDecimals } from 'src/utils'
import { runAfterNextPaint } from 'src/utils/afterNextPaint'
import { getCoverPhotoUrl, getDiscount } from 'src/utils/coverPhoto'
import { operatorPromoPill } from 'src/utils/operatorPromo'
import { trackSearchResultClick } from 'src/utils/pixels/searchEvents'
import { buildCardBadges } from 'src/utils/searchAnalytics'
import { ILocation } from '../../types/graphql'
import CardLocationLink from '../CardLinks/CardLocationLink'

const NON_BREAKING_HYPHEN = '\u2011'

export const FloatingCardContainer = ({
  children,
  pbm
}: {
  children: React.ReactNode
  pbm?: string
}) => (
  <div
    className={`pointer-events-none absolute bottom-0 left-1/2 z-10 flex w-full -translate-x-1/2 flex-col items-center justify-end ${pbm ? pbm : 'pb-6'} md:pb-6`}
  >
    <div className="w-[85vw] sm:w-[360px] md:w-[400px]">{children}</div>
  </div>
)

interface SpotCardProps {
  location: ILocation
  onMarkerClick: (marker?: ILocation | undefined) => void
}

const LocationPreviewCard = ({ location, onMarkerClick }: SpotCardProps) => {
  const { t } = useTranslation('common')
  const { isDownMd } = useBreakpoints()
  // Same label as the search card — a listing must not read differently in the
  // map popup than in the result list next to it. `shortAddress` is the
  // fallback for callers whose query predates `cardLocation`.
  const { defaultConfiguration, shortAddress, cardLocation } = location
  const locationLabel = cardLocation || shortAddress
  const { canStoreVehicle, locationType } = defaultConfiguration ?? {}
  // `locationType` first — same reason as SearchCard: the CARD fragment
  // doesn't ship `canStoreVehicle`, so the bare check never fired on card
  // data. `isVehicleLocationType` is the canonical vehicle check.
  const isParking =
    isVehicleLocationType(locationType) || canStoreVehicle === true
  const {
    hasMultipleConfigurations,
    numConfigurations,
    sizeRangeText,
    totalAvailability
  } = useConfigSummary(location)
  // Same semantics as SearchCard: parking pill shows AVAILABLE units; the
  // summary sum covers multi-config, defaultConfiguration is the fallback
  // (thin/legacy queries, and ISR pages prerendered before
  // `totalAvailability` shipped — a stale static payload lacks the key
  // despite the `Int!` schema type). Don't "simplify" the `??` chain away.
  const availableSpots = isParking
    ? (totalAvailability ?? defaultConfiguration?.availability ?? null)
    : null
  // Admin-only landlord label (sales team) — resolved by the search
  // surface's AdminHostBadgesProvider, absent for every other viewer.
  const adminHostBadge = useAdminHostBadge(location.id)
  const { price, isBrokerMode, showFromPrice } = useCardPrice(location)
  const { locMap, showSize } = useCommon()
  const locType = locMap(location)
  const discount = getDiscount(defaultConfiguration, isBrokerMode)
  // The operator's own promotion (Traditional only — never co-occurs with
  // `discount`, which is SpotMe Now only).
  const promoPill = operatorPromoPill(defaultConfiguration?.operatorPromo)
  const promoBadge = promoBadgeContent(discount, promoPill)

  const { filters } = useFilterProvider()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const entries = Object.entries(filters).filter(([, v]) => !!v) as any
  const params = new URLSearchParams(entries)
  const urlRoute = defaultConfiguration?.urlRoute ?? location.urlRoute ?? ''
  const url = isDownMd ? `${urlRoute}?${params.toString()}` : urlRoute

  const [mounted, setMounted] = useState<string>(location.id)
  useEffect(() => {
    setMounted(location.id)
  }, [location.id])
  if (mounted !== location.id) return null

  const sizeText = sizeRangeText ?? showSize(location, true)

  const [iconBig, value, unit] = isBrokerMode
    ? [
        <CropFreeIcon className="mt-[-2.5px] mr-1 text-lg" key="iconBig" />,
        price,
        '/m²'
      ]
    : [null, price, `/${t('FILTER.MONTH')}`]

  return (
    <div className="pointer-events-auto relative">
      {/* Close Button */}
      <div className="absolute top-2 right-2 z-10">
        <button
          aria-label="Close"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onMarkerClick()
          }}
          className="flex size-7 items-center justify-center rounded-full bg-white-500/90 shadow-md backdrop-blur-sm transition-all hover:bg-white-500 hover:shadow-lg"
        >
          <Close className="size-3.5 text-black-500" />
        </button>
      </div>

      <CardLocationLink urlRoute={url ?? ''}>
        <div
          onClick={() => {
            // Discovery event, fired after the tap paints — gtag inside the
            // tap cost ~100 ms at 4× CPU (see src/utils/afterNextPaint.ts).
            const clickArgs = {
              surface: 'map_card' as const,
              configId: defaultConfiguration?.id,
              locationId: location.id,
              badges: buildCardBadges(location, isBrokerMode)
            }
            runAfterNextPaint(() => trackSearchResultClick(clickArgs))
          }}
          className="w-full overflow-hidden rounded-2xl border border-ink-100/15 bg-white-500 shadow-[0_8px_24px_-12px_rgba(17,29,42,0.18)]"
        >
          <div className="flex flex-row">
            {/* Image Section */}
            <div className="relative w-2/5 min-w-[100px]">
              <div className="relative size-full">
                <CardImage
                  src={getCoverPhotoUrl(location)}
                  alt={`${locType} ${sizeText} en ${shortAddress}`}
                  eager
                />
              </div>
            </div>

            {/* Content Section */}
            <div className="min-w-0 flex-1 border-l border-ink-100/15 p-2.5 md:p-3">
              {/* Header with type and verified icon — `pr-7` keeps the
                  title clear of the absolute close button. At 320px the
                  copy column is ~115px, so the title wraps to two lines at
                  a space («Bodega / Comercial»), and a single long word gets a
                  Spanish hyphen («Estaciona-miento», html lang="es") instead
                  of being clipped by the clamp. The non-breaking hyphen
                  stops «Mini-Bodega» splitting at its own hyphen. */}
              <div className="mb-1 flex items-start gap-1 pr-7">
                <p className="line-clamp-2 min-w-0 text-sm leading-snug font-semibold tracking-[-0.005em] hyphens-auto text-ink-700 md:text-base">
                  {locType.replace(/-/g, NON_BREAKING_HYPHEN)}
                </p>
                <VerifiedIcon className="mt-0.5 size-4 shrink-0 text-blue-500" />
              </div>

              {/* Admin-only landlord chip (sales team) — in the copy column,
                  matching SearchCard: the 100px photo has no room for an
                  operator name, and truncating one there is useless. */}
              {adminHostBadge && (
                <div
                  title={adminHostBadge.name}
                  className="mb-1 flex w-fit max-w-full items-center gap-1 rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[10px] leading-tight font-semibold text-blue-500"
                >
                  {adminHostBadge.isTeam ? (
                    <BusinessIcon className="size-3 shrink-0" />
                  ) : (
                    <PersonIcon className="size-3 shrink-0" />
                  )}
                  <span className="truncate">{adminHostBadge.name}</span>
                </div>
              )}

              <p className="mb-1.5 line-clamp-1 text-xs text-ink-200">
                {locationLabel}
              </p>

              {/* Price section — amount + unit never split; «Desde» is its
                  own label line, as on the search card. */}
              <span className="block text-blue-500">
                {showFromPrice && (
                  <span className="block text-xs leading-tight font-medium text-ink-200">
                    {t('SEARCH_CARD.FROM_PRICE')}
                  </span>
                )}
                <span className="whitespace-nowrap">
                  {iconBig}
                  <span className="font-display text-lg font-medium tracking-[-0.01em] italic md:text-xl">
                    {`$${numberRounedUpWithoutDecimals(value)}`}
                  </span>
                  <span className="text-sm font-medium text-ink-200">
                    {unit}
                  </span>
                </span>
              </span>
            </div>
          </div>
          {/* Same one-line footer as the search card it pops up from: the
              copy column is ~140px at 320px, where the size pill and
              «50% 1er mes · 6+ meses» cannot share a line (Daniel 2026-10-26,
              Jules 2026-10-28). Promo first — never the chip that drops. */}
          <CardFactsStrip className="md:px-3">
            {promoBadge && <PromoBadge promo={promoBadge} placement="inline" />}
            {/* Parking: AVAILABLE units when known — single- and multi-
                config alike; config-count fallback for thin shapes;
                single-config with no availability data shows no chip.
                Otherwise (storage) size + count. */}
            {isParking &&
            !hasMultipleConfigurations &&
            availableSpots == null ? null : (
              <FactChip>
                {isParking ? (
                  <span className="font-semibold text-blue-500">
                    {availableSpots != null
                      ? t('SEARCH_CARD.AVAILABLE_SPOTS', {
                          count: availableSpots
                        })
                      : t('SEARCH_CARD.SPOTS', { count: numConfigurations })}
                  </span>
                ) : (
                  <>
                    {hasMultipleConfigurations && (
                      <span className="font-semibold text-blue-500">
                        {numConfigurations}
                        <span className="mx-0.5 text-ink-200">·</span>
                      </span>
                    )}
                    {sizeText}
                  </>
                )}
              </FactChip>
            )}
          </CardFactsStrip>
        </div>
      </CardLocationLink>
    </div>
  )
}

export default React.memo(LocationPreviewCard)
